import {
	makeApiRequest,
	makeBinanceApiRequest,
	getExchangeInfo,
	generateSymbol
} from './helpers.js';
import {
	startBinanceStream
} from './binanceStreaming.js';
import { findCandleForCursor, normalizeChartResolution } from './candleLookup.js';

const lastBarsCache = new Map();
const loadedBarsCache = new Map();
const intervalMap = new Map();
const streamUnsubscribers = new Map();

export function getBAPI() {
	const isSpot = window.__isSpot ? window.__isSpot() : false;
	return {
		version: isSpot ? "v3" : "v1",
		host: isSpot ? "api" : "fapi"
	};
}

export const BAPI = getBAPI();

intervalMap.set('1', '1m');
intervalMap.set('5', '5m');
intervalMap.set('15', '15m');
intervalMap.set('240', '4h');
intervalMap.set('1D', '1d');
intervalMap.set('1W', '1w');

function getLastBarCacheKey(symbolInfo, resolution) {
	return `${symbolInfo.ticker || symbolInfo.full_name || symbolInfo.name}|${resolution}`;
}

function getLoadedBarsKey(symbol, resolution) {
	return `${String(symbol || '').replace(/[^a-z0-9]/gi, '').toUpperCase()}|${resolution}`;
}

function rememberLoadedBars(symbolInfo, resolution, bars) {
	const aliases = [symbolInfo.ticker, symbolInfo.full_name, symbolInfo.name]
		.filter(Boolean)
		.map((symbol) => getLoadedBarsKey(symbol, resolution));
	const canonicalKey = getLoadedBarsKey(symbolInfo.name || symbolInfo.ticker || symbolInfo.full_name, resolution);
	const byTime = loadedBarsCache.get(canonicalKey) || new Map();
	for (const bar of bars || []) {
		if (bar?.time != null) byTime.set(Number(bar.time), { ...bar, observedAt: Date.now() });
	}
	while (byTime.size > 3000) byTime.delete(byTime.keys().next().value);
	for (const key of new Set([canonicalKey, ...aliases])) {
		loadedBarsCache.set(key, byTime);
	}
}

export function getLoadedBarsSnapshot(symbol, resolution, limit) {
	const normalizedResolution = normalizeChartResolution(resolution);
	const byTime = loadedBarsCache.get(getLoadedBarsKey(symbol, resolution))
		|| loadedBarsCache.get(getLoadedBarsKey(symbol, normalizedResolution));
	if (!byTime) return [];

	const bars = Array.from(byTime.values())
		.filter((bar) => Number.isFinite(Number(bar?.time)))
		.sort((left, right) => Number(left.time) - Number(right.time));
	const boundedLimit = Math.max(0, Number(limit) || bars.length);
	return bars.slice(-boundedLimit).map((bar) => ({ ...bar }));
}

window.__insightsCandleAt = (symbol, resolution, unixTime) => {
	const normalizedResolution = normalizeChartResolution(resolution);
	const byTime = loadedBarsCache.get(getLoadedBarsKey(symbol, resolution))
		|| loadedBarsCache.get(getLoadedBarsKey(symbol, normalizedResolution));
	return findCandleForCursor(byTime, unixTime, normalizedResolution);
};

// DatafeedConfiguration implementation
const configurationData = {
	// Represents the resolutions for bars supported by your datafeed
	supported_resolutions: ['1', '5', '15', '240', '1D', '1W'],
	
	// The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
	exchanges: [{
		value: 'Binance',
		name: 'Binance',
		desc: 'Binance',
	},
	],
	// The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
	symbols_types: [{
		name: 'crypto',
		value: 'crypto',
	},
	],
};

// Obtains all symbols for all exchanges supported by CryptoCompare API
async function getAllSymbols() {
	const data = await makeApiRequest(`data/v3/all/exchanges`);
	let allSymbols = [];

	for (const exchange of configurationData.exchanges) {
		const pairs = data.Data[exchange.value].pairs;

		for (const leftPairPart of Object.keys(pairs)) {
			const symbols = pairs[leftPairPart].map(rightPairPart => {
				const symbol = generateSymbol(exchange.value, leftPairPart, rightPairPart);
				return {
					symbol: symbol.short,
					full_name: symbol.full,
					description: symbol.short,
					exchange: exchange.value,
					type: 'crypto',
				};
			});
			allSymbols = [...allSymbols, ...symbols];
		}
	}
	return allSymbols;
}

const Datafeed = {

	onReady: (callback) => {
		console.log('[onReady]: Method call');
		setTimeout(() => callback(configurationData));
	},

	searchSymbols: async (
		userInput,
		exchange,
		symbolType,
		onResultReadyCallback,
	) => {
		console.log('[searchSymbols]: Method call');
		const symbols = await getAllSymbols();
		const newSymbols = symbols.filter(symbol => {
			const isExchangeValid = exchange === '' || symbol.exchange === exchange;
			const isFullSymbolContainsInput = symbol.full_name
				.toLowerCase()
				.indexOf(userInput.toLowerCase()) !== -1;
			return isExchangeValid && isFullSymbolContainsInput;
		});
		onResultReadyCallback(newSymbols);
	},

	resolveSymbol: async (
		symbolName,
		onSymbolResolvedCallback,
		onResolveErrorCallback,
		extension
	) => {
		var ei = await getExchangeInfo();
		const sName = symbolName.substring(symbolName.indexOf(":") + 1, symbolName.length);
		const eiSymbol = ei.get(sName.replace('/',''));
		let priceScale = Math.ceil(1 / Number(eiSymbol ? eiSymbol.filters[0].tickSize : "10000000"));
		// Symbol information object
		const symbolInfo = {
			ticker: symbolName,
			name: sName,
			description: symbolName,
			type: "crypto",
			session: '24x7',
			timezone: 'Etc/UTC',
			exchange: symbolName.substring(0, symbolName.indexOf(":")),
			pricescale: priceScale,
      has_intraday: true,
      visible_plots_set: 'ohlc',
			supported_resolutions: configurationData.supported_resolutions,
			volume_precision: 2,
			data_status: 'streaming',
			intraday_multipliers: ['5', '15', '240']
		};

		console.log('[resolveSymbol]: Symbol resolved', symbolName);
		onSymbolResolvedCallback(symbolInfo);
	},

	getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
		const { from, to, firstDataRequest } = periodParams;
		console.log('[getBars]: Method call', symbolInfo, resolution, from, to);
		const urlParameters = {
			symbol: symbolInfo.name.replace('/',''),
			interval: intervalMap.get(resolution),
			limit: 1000,
			startTime: from * 1000,
			endTime: to * 1000
		};
		const query = Object.keys(urlParameters)
			.map(name => `${name}=${encodeURIComponent(urlParameters[name])}`)
			.join('&');
		try {
			const bapi = getBAPI();
			const data = await makeBinanceApiRequest(`${bapi.host}/${bapi.version}/klines?${query}`);
			if (!data || data.length === 0) {
				// "noData" should be set if there is no data in the requested period
				onHistoryCallback([], {
					noData: true,
				});
				return;
			}
			let bars = [];
			//data.reverse();
			data.forEach(bar => {
				if (bar.at(0) >= from * 1000 && bar.at(0) < to * 1000) {
					bars = [...bars, {
						time: bar.at(0),
						low: Number(bar.at(3)),
						high: Number(bar.at(2)),
						open: Number(bar.at(1)),
						close: Number(bar.at(4)),
						volume: Number(bar.at(5))
					}];
				}
			});
			if (firstDataRequest) {
				lastBarsCache.set(getLastBarCacheKey(symbolInfo, resolution), {
					...bars[bars.length - 1],
				});
			}
			rememberLoadedBars(symbolInfo, resolution, bars);
			console.log(`[getBars]: returned ${bars.length} bar(s)`);
			onHistoryCallback(bars, {
				noData: false,
			});
		} catch (error) {
			console.log('[getBars]: Get error', error);
			onErrorCallback(error);
		}
	},

	subscribeBars: (
		symbolInfo,
		resolution,
		onRealtimeCallback,
		subscriberUID,
		onResetCacheNeededCallback,
	) => {
		console.log('[subscribeBars]: Method call with subscriberUID:', subscriberUID);
		const symbolData = symbolInfo.name.replace('/', '').toLowerCase();
		const isSpot = window.__isSpot ? window.__isSpot() : false;
		const interval = intervalMap.get(resolution);
		const cacheKey = getLastBarCacheKey(symbolInfo, resolution);
		const lastBar = lastBarsCache.get(cacheKey);

		if (!interval) {
			console.error('[subscribeBars]: Unsupported resolution:', resolution);
			return;
		}

		const previousUnsubscribe = streamUnsubscribers.get(subscriberUID);
		if (previousUnsubscribe) {
			previousUnsubscribe();
		}

		const unsubscribe = startBinanceStream(
			symbolData,
			interval,
			(bar) => {
				lastBarsCache.set(cacheKey, { ...bar });
				rememberLoadedBars(symbolInfo, resolution, [bar]);
				onRealtimeCallback(bar);
			},
			isSpot,
			lastBar
		);

		streamUnsubscribers.set(subscriberUID, unsubscribe);
	},

	unsubscribeBars: (subscriberUID) => {
		console.log('[unsubscribeBars]: Method call with subscriberUID:', subscriberUID);
		const unsubscribe = streamUnsubscribers.get(subscriberUID);

		if (unsubscribe) {
			unsubscribe();
			streamUnsubscribers.delete(subscriberUID);
		}
	},
};

// Track candles delivered to this widget, not the shared cache of prior charts.
// TradingView moves drawings whose timestamps have no loaded data point.
export function createChartDatafeed(symbol, resolution) {
	const key = getLoadedBarsKey(symbol, normalizeChartResolution(resolution));
	const candles = new Map();
	let firstTime = null;
	let lastTime = null;
	const remember = (info, interval, bars, reset = false) => {
		if (getLoadedBarsKey(info.name, normalizeChartResolution(interval)) !== key) return;
		if (reset) { candles.clear(); firstTime = null; lastTime = null; }
		for (const bar of bars) {
			const time = Number(bar.time);
			if (!Number.isFinite(time)) continue;
			candles.set(time, { time, high: bar.high, low: bar.low });
			firstTime = firstTime == null ? time : Math.min(firstTime, time);
			lastTime = lastTime == null ? time : Math.max(lastTime, time);
		}
	};
	return {
		...Datafeed,
		candleAt: time => findCandleForCursor(candles, time, resolution),
		loadedRange: () => firstTime == null ? null : { from: firstTime / 1000, to: lastTime / 1000 },
		getBars(info, interval, params, onHistory, onError) {
			return Datafeed.getBars(info, interval, params, (bars, metadata) => {
				remember(info, interval, bars, params.firstDataRequest);
				onHistory(bars, metadata);
			}, onError);
		},
		subscribeBars(info, interval, onRealtime, subscriberUID, onReset) {
			return Datafeed.subscribeBars(info, interval, bar => {
				remember(info, interval, [bar]);
				onRealtime(bar);
			}, subscriberUID, onReset);
		}
	};
}

export default Datafeed;
